import React, { useEffect, useRef, useState } from "react";
import type Phaser from "phaser";
import { WORLD_WIDTH, WORLD_HEIGHT } from "../game/types";

interface GameMinimapProps {
  gameRef: React.MutableRefObject<Phaser.Game | null>;
  playerPosition: { x: number; y: number };
  destination: { x: number; y: number; name?: string } | null;
}

interface MinimapIsland {
  x: number;
  y: number;
  biome: string;
  isPermanent: boolean;
  id?: string;
  permanentName?: string | null;
}

interface MinimapSearchOption {
  id: string;
  label: string;
  type: "port" | "resource" | "fishing";
  value: string;
}

const BIOME_COLORS: Record<string, string> = {
  beach: "#c2b280",
  forest: "#228b22",
  mountain: "#7f8c8d",
  jungle: "#1e8449",
  desert: "#e67e22",
  volcanic: "#c0392b",
  winter: "#f0f8ff",
  mixed: "#4a7c23",
};

const MAP_SIZE = 100;
const MAP_PADDING = 0;
const MAP_ZOOM = 2;

export const GameMinimap: React.FC<GameMinimapProps> = ({
  gameRef,
  playerPosition,
  destination,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [islands, setIslands] = useState<MinimapIsland[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const islandsRef = useRef<MinimapIsland[]>([]);
  const playerRef = useRef(playerPosition);
  const destinationRef = useRef(destination);

  useEffect(() => {
    playerRef.current = playerPosition;
  }, [playerPosition]);

  useEffect(() => {
    destinationRef.current = destination;
  }, [destination]);

  useEffect(() => {
    islandsRef.current = islands;
  }, [islands]);

  useEffect(() => {
    let isMounted = true;

    const updateIslands = () => {
      const scene = gameRef.current?.scene.getScene("OceanScene") as any;
      const islandRenderer = scene?.islandRenderer;
      const islandMap = islandRenderer?.getIslands?.();

      if (!islandMap || !(islandMap instanceof Map)) return;

      const next: MinimapIsland[] = [];
      islandMap.forEach((entry: any) => {
        const data = entry?.islandData;
        if (!data) return;

        next.push({
          x: data.x,
          y: data.y,
          biome: data.biome || "mixed",
          isPermanent: Boolean(data.isPermanent),
          id: data.id,
          permanentName: data.permanentName,
        });
      });

      if (isMounted) {
        setIslands(next);
      }
    };

    updateIslands();
    const interval = window.setInterval(updateIslands, 1000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [gameRef]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      const innerSize = width - MAP_PADDING * 2;

      ctx.imageSmoothingEnabled = false;

      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#0a1628";
      ctx.fillRect(0, 0, width, height);

      ctx.strokeStyle = "rgba(48, 54, 61, 0.9)";
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

      ctx.strokeStyle = "rgba(56, 80, 110, 0.35)";
      for (let i = 1; i < 8; i += 1) {
        const offset = MAP_PADDING + (i / 8) * innerSize;
        ctx.beginPath();
        ctx.moveTo(offset, MAP_PADDING);
        ctx.lineTo(offset, MAP_PADDING + innerSize);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(MAP_PADDING, offset);
        ctx.lineTo(MAP_PADDING + innerSize, offset);
        ctx.stroke();
      }

      const viewWidth = WORLD_WIDTH / MAP_ZOOM;
      const viewHeight = WORLD_HEIGHT / MAP_ZOOM;
      const viewLeft = Math.max(
        0,
        Math.min(WORLD_WIDTH - viewWidth, playerRef.current.x - viewWidth / 2),
      );
      const viewTop = Math.max(
        0,
        Math.min(
          WORLD_HEIGHT - viewHeight,
          playerRef.current.y - viewHeight / 2,
        ),
      );

      const drawnIslands = new Set<string>();
      islandsRef.current.forEach((island) => {
        const relX = island.x - viewLeft;
        const relY = island.y - viewTop;
        if (relX < 0 || relY < 0 || relX > viewWidth || relY > viewHeight)
          return;

        const mapX = MAP_PADDING + (relX / viewWidth) * innerSize;
        const mapY = MAP_PADDING + (relY / viewHeight) * innerSize;
        const size = island.isPermanent ? 3 : 2;
        const half = size / 2;
        const color = island.isPermanent
          ? "#ffd700"
          : BIOME_COLORS[island.biome] || BIOME_COLORS.mixed;

        if (!island.isPermanent) {
          const islandKey = `${Math.round(mapX)}:${Math.round(mapY)}`;
          if (drawnIslands.has(islandKey)) return;
          drawnIslands.add(islandKey);
          ctx.fillStyle = color;
          ctx.fillRect(
            Math.round(mapX - half),
            Math.round(mapY - half),
            size,
            size,
          );
          return;
        }

        ctx.fillStyle = color;
        ctx.fillRect(Math.round(mapX - 2), Math.round(mapY - 2), 4, 4);
        ctx.strokeStyle = "rgba(255, 215, 0, 0.75)";
        ctx.strokeRect(Math.round(mapX - 3), Math.round(mapY - 3), 6, 6);
      });

      // Player is always at the center of the minimap
      const centerX = MAP_PADDING + innerSize / 2;
      const centerY = MAP_PADDING + innerSize / 2;

      ctx.fillStyle = "#58a6ff";
      ctx.fillRect(Math.round(centerX - 2), Math.round(centerY - 2), 4, 4);

      const currentDestination = destinationRef.current;
      if (currentDestination) {
        const destRelX = currentDestination.x - viewLeft;
        const destRelY = currentDestination.y - viewTop;
        if (
          destRelX < 0 ||
          destRelY < 0 ||
          destRelX > viewWidth ||
          destRelY > viewHeight
        ) {
          const playerPos = playerRef.current;
          const dx = currentDestination.x - playerPos.x;
          const dy = currentDestination.y - playerPos.y;
          const angle = Math.atan2(dy, dx);
          const radius = innerSize / 2 - 6;
          const tipX = centerX + Math.cos(angle) * radius;
          const tipY = centerY + Math.sin(angle) * radius;
          const leftAngle = angle + 2.5;
          const rightAngle = angle - 2.5;

          ctx.fillStyle = "#ff6b6b";
          ctx.beginPath();
          ctx.moveTo(tipX, tipY);
          ctx.lineTo(
            tipX + Math.cos(leftAngle) * 6,
            tipY + Math.sin(leftAngle) * 6,
          );
          ctx.lineTo(
            tipX + Math.cos(rightAngle) * 6,
            tipY + Math.sin(rightAngle) * 6,
          );
          ctx.closePath();
          ctx.fill();
        } else {
          const destX = MAP_PADDING + (destRelX / viewWidth) * innerSize;
          const destY = MAP_PADDING + (destRelY / viewHeight) * innerSize;
          ctx.fillStyle = "#ff6b6b";
          ctx.fillRect(Math.round(destX - 2), Math.round(destY - 2), 4, 4);
        }
      }
    };

    draw();
    const interval = window.setInterval(draw, 200);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!searchRef.current) return;
      const dropdown = searchRef.current.querySelector(
        ".game-minimap-search-dropdown",
      );
      if (dropdown && dropdown.contains(event.target as Node)) return;
      const searchButton = searchRef.current.querySelector(
        ".game-minimap-search",
      );
      if (searchButton && searchButton.contains(event.target as Node)) return;
      setSearchOpen(false);
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClick, true);
      return () => document.removeEventListener("mousedown", handleClick, true);
    }
  }, [searchOpen]);

  const getSearchOptions = (): MinimapSearchOption[] => {
    const ports = [
      {
        id: "port_trading_post",
        label: "Trading Post",
        type: "port" as const,
        value: "shop",
      },
      {
        id: "port_shipyard",
        label: "Shipyard",
        type: "port" as const,
        value: "shipyard",
      },
      {
        id: "port_harbor_office",
        label: "Harbor Office",
        type: "port" as const,
        value: "courier",
      },
      {
        id: "port_tavern",
        label: "Tavern",
        type: "port" as const,
        value: "crew_recruitment",
      },
    ];

    const resources = [
      {
        id: "fish_shallow",
        label: "Fishing: Shallow Waters",
        type: "fishing" as const,
        value: "shallow_waters",
      },
      {
        id: "fish_deep",
        label: "Fishing: Deep Sea",
        type: "fishing" as const,
        value: "deep_sea",
      },
      {
        id: "fish_coral",
        label: "Fishing: Coral Reef",
        type: "fishing" as const,
        value: "coral_reef",
      },
      {
        id: "fish_hole",
        label: "Fishing: Fishing Hole",
        type: "fishing" as const,
        value: "fishing_hole",
      },
      {
        id: "fish_shipwreck",
        label: "Fishing: Shipwreck Pool",
        type: "fishing" as const,
        value: "shipwreck_pool",
      },
      {
        id: "fish_volcanic",
        label: "Fishing: Volcanic Waters",
        type: "fishing" as const,
        value: "volcanic_waters",
      },
      {
        id: "fish_abyssal",
        label: "Fishing: Abyssal Trench",
        type: "fishing" as const,
        value: "abyssal_trench",
      },
      {
        id: "fish_whirlpool",
        label: "Fishing: Whirlpool Edge",
        type: "fishing" as const,
        value: "whirlpool_edge",
      },
      {
        id: "fish_moonlit",
        label: "Fishing: Moonlit Shallows",
        type: "fishing" as const,
        value: "moonlit_shallows",
      },
      {
        id: "fish_ancient",
        label: "Fishing: Ancient Grounds",
        type: "fishing" as const,
        value: "ancient_grounds",
      },
      {
        id: "res_shipwreck",
        label: "Salvaging: Shipwreck Debris",
        type: "resource" as const,
        value: "shipwreck_debris",
      },
      {
        id: "res_rowboat",
        label: "Salvaging: Sunken Rowboat",
        type: "resource" as const,
        value: "sunken_rowboat",
      },
      {
        id: "res_merchant",
        label: "Salvaging: Merchant Wreck",
        type: "resource" as const,
        value: "merchant_wreck",
      },
      {
        id: "res_cargo",
        label: "Salvaging: Abandoned Cargo",
        type: "resource" as const,
        value: "abandoned_cargo",
      },
      {
        id: "res_naval",
        label: "Salvaging: Naval Wreck",
        type: "resource" as const,
        value: "naval_wreck",
      },
      {
        id: "res_ancient_ruins",
        label: "Salvaging: Ancient Ruins",
        type: "resource" as const,
        value: "ancient_ruins",
      },
      {
        id: "res_stone",
        label: "Mining: Stone Quarry",
        type: "resource" as const,
        value: "stone_quarry",
      },
      {
        id: "res_tin",
        label: "Mining: Tin Deposit",
        type: "resource" as const,
        value: "tin_deposit",
      },
      {
        id: "res_mineral",
        label: "Mining: Mineral Outcrop",
        type: "resource" as const,
        value: "mineral_outcrop",
      },
      {
        id: "res_silver",
        label: "Mining: Silver Vein",
        type: "resource" as const,
        value: "silver_vein",
      },
      {
        id: "res_gold",
        label: "Mining: Gold Deposit",
        type: "resource" as const,
        value: "gold_deposit",
      },
      {
        id: "res_coal",
        label: "Mining: Coal Seam",
        type: "resource" as const,
        value: "coal_seam",
      },
      {
        id: "res_volcanic",
        label: "Mining: Volcanic Vent",
        type: "resource" as const,
        value: "volcanic_vent",
      },
      {
        id: "res_driftwood",
        label: "Foraging: Driftwood Cluster",
        type: "resource" as const,
        value: "driftwood_cluster",
      },
      {
        id: "res_coconut",
        label: "Foraging: Coconut Palm",
        type: "resource" as const,
        value: "coconut_palm",
      },
      {
        id: "res_seaweed",
        label: "Foraging: Seaweed Bed",
        type: "resource" as const,
        value: "seaweed_bed",
      },
      {
        id: "res_herb",
        label: "Foraging: Herb Patch",
        type: "resource" as const,
        value: "herb_patch",
      },
      {
        id: "res_fruit",
        label: "Foraging: Fruit Grove",
        type: "resource" as const,
        value: "fruit_grove",
      },
      {
        id: "res_mushroom",
        label: "Foraging: Mushroom Colony",
        type: "resource" as const,
        value: "mushroom_colony",
      },
      {
        id: "res_flower",
        label: "Foraging: Rare Flower Bed",
        type: "resource" as const,
        value: "rare_flower_bed",
      },
    ];

    return [...ports, ...resources];
  };

  const handleSearchSelection = (option: MinimapSearchOption) => {
    const scene = gameRef.current?.scene.getScene("OceanScene") as any;
    const socket = scene?.socket;
    if (!scene || !socket) return;

    socket.emit("minimap:findNearest", {
      kind: option.type,
      type: option.value,
    });
    setSearchOpen(false);
  };

  return (
    <div className="game-minimap-container" ref={searchRef}>
      <div className="game-minimap-header">
        <span>MINIMAP</span>
        <button
          type="button"
          className="game-minimap-search"
          onClick={() => setSearchOpen((prev) => !prev)}
          aria-label="Search minimap targets"
        >
          🔍
        </button>
        {searchOpen && (
          <div className="game-minimap-search-dropdown">
            {getSearchOptions().map((option) => (
              <button
                key={option.id}
                type="button"
                className="game-minimap-search-item"
                onClick={() => handleSearchSelection(option)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <canvas
        ref={canvasRef}
        className="game-minimap-canvas"
        width={MAP_SIZE}
        height={MAP_SIZE}
      />
    </div>
  );
};
